import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useForm from '@/hooks/form/use-form.hook'

import { ApplicationState } from '@/state/root.models'
import { TypeRestriction } from '@/enums'
import { Restriction } from '@/state/bonus-engine/models'

import { StateSchema } from '@/hooks/form/models'
import { authSelector } from '@/state/auth/slices/index.slices'
import { rolloverRestrictionsSchema } from '../components/schema'

import { rolloverRestrictionsValidator } from '../components/validator'
import useRestrictionsSportsTypes from './use-restrictions-sports-types.hook'

const useRolloverRestrictions = () => {
  const { currentBonus } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const { brand } = useSelector(authSelector)

  const { state, handleOnChange, disable, setFormValues, setDisable } = useForm(
    rolloverRestrictionsSchema(brand.currencies),
    rolloverRestrictionsValidator(brand.currencies),
    () => {},
  )

  const {
    getSportDefaultValues,
    getProductsDefaultValues,
    getWagerTypeDefaultValues,
  } = useRestrictionsSportsTypes()

  const setCurrentRolloverRestrictions = () => {
    let schema: StateSchema = {}

    currentBonus.freePlayRolloverRestrictions?.minResetRealMoneyAmount &&
    currentBonus.freePlayRolloverRestrictions?.minResetRealMoneyAmount.length >
      0
      ? currentBonus.freePlayRolloverRestrictions?.minResetRealMoneyAmount.forEach(
          (item) => {
            schema = {
              ...schema,
              [`minResetRealMoneyAmount_${item.currency}`]: {
                value: item.amount > 0 ? item.amount?.toString() : '',
                error: '',
              },
            }
          },
        )
      : brand.currencies.forEach((currency) => {
          schema = {
            ...schema,
            [`minResetRealMoneyAmount_${currency}`]: {
              value: '',
              error: '',
            },
          }
        })

    setFormValues({
      /* TODO: We could use this rollover type in the future
      products: {
        value: currentBonus.freePlayRolloverRestrictions?.products
          ? currentBonus.freePlayRolloverRestrictions?.products.map(
              (product: Restriction) => {
                return {
                  type: product.type,
                  validationId: Number(product.validationId),
                  action: product.action,
                }
              },
            )
          : [],
        error: '',
      }, */
      sports: {
        value: currentBonus.freePlayRolloverRestrictions?.sports
          ? currentBonus.freePlayRolloverRestrictions?.sports.map(
              (sport: Restriction) => {
                return {
                  type: sport.type,
                  validationId: sport.validationId,
                  action: sport.action,
                }
              },
            )
          : [],
        error: '',
      },
      wagerTypes: {
        value: currentBonus.freePlayRolloverRestrictions?.wagerTypes
          ? currentBonus.freePlayRolloverRestrictions?.wagerTypes.map(
              (wager: Restriction) => {
                return {
                  type: wager.type,
                  validationId: Number(wager.validationId),
                  action: wager.action,
                }
              },
            )
          : [],
        error: '',
      },
      maxLine: {
        value: currentBonus.freePlayRolloverRestrictions?.line?.max,
        error: '',
      },
      minLine: {
        value: currentBonus.freePlayRolloverRestrictions?.line?.min,
        error: '',
      },
      rolloverAmountCalculationType: {
        value:
          currentBonus.freePlayRolloverRestrictions
            ?.rolloverAmountCalculationType,
        error: '',
      },
      ...schema,
    })

    setDisable(false)
  }

  // SPORTS RESTRICTIONS

  const setItem = (type: string, item: Restriction): void => {
    if (type === 'product') {
      const filterList = state.products.value.filter(
        (product: Restriction) => product.validationId !== item.validationId,
      )
      setFormValues({
        ...state,
        products: {
          value: [...filterList, item] || [],
          error: '',
        },
      })
    } else if (type === 'wagerType') {
      const filterList = state.wagerTypes.value.filter(
        (wagerType: Restriction) =>
          wagerType.validationId !== item.validationId,
      )
      setFormValues({
        ...state,
        wagerTypes: {
          value: [...filterList, item] || [],
          error: '',
        },
      })
    } else {
      const filterList = state.sports.value.filter(
        (sport: Restriction) => sport.validationId !== item.validationId,
      )
      setFormValues({
        ...state,
        sports: {
          value: [...filterList, item] || [],
          error: '',
        },
      })
    }
  }

  const handleOnClickSetEnableRollover = (
    type: string,
    value: number | string,
    all?: boolean,
  ) => {
    if (!all) {
      const item = {
        type: Number(
          Object.keys(TypeRestriction)[
            Object.values(TypeRestriction).indexOf(type)
          ],
        ),
        validationId: value,
        action: 1,
      }
      setItem(type, item)
    } else {
      handleUpdateAllItemsRollover(type, 1)
    }
  }

  const handleOnClickSetDisableRollover = (
    type: string,
    value: number | string,
    all?: boolean,
  ) => {
    if (!all) {
      const item = {
        type: Number(
          Object.keys(TypeRestriction)[
            Object.values(TypeRestriction).indexOf(type)
          ],
        ),
        validationId: value,
        action: 2,
      }
      setItem(type, item)
    } else {
      handleUpdateAllItemsRollover(type, 2)
    }
  }

  const handleUpdateAllItemsRollover = (type: string, action: number) => {
    if (type === 'product') {
      const filterList = state.products.value.map((product: Restriction) => {
        return {
          ...product,
          action,
        }
      })
      setFormValues({
        ...state,
        products: {
          value: [...filterList] || [],
          error: '',
        },
      })
    } else if (type === 'wagerType') {
      const filterList = state.wagerTypes.value.map((wager: Restriction) => {
        return {
          ...wager,
          action,
        }
      })
      setFormValues({
        ...state,
        wagerTypes: {
          value: [...filterList] || [],
          error: '',
        },
      })
    } else {
      const filterList = state.sports.value.map((sport: Restriction) => {
        return {
          ...sport,
          action,
        }
      })
      setFormValues({
        ...state,
        sports: {
          value: [...filterList] || [],
          error: '',
        },
      })
    }
  }

  const handleOnClickSetDiscardContributionRollover = (
    type: string,
    value: number | string,
  ) => {
    const item = {
      type: Number(
        Object.keys(TypeRestriction)[
          Object.values(TypeRestriction).indexOf(type)
        ],
      ),
      validationId: value,
      action: 3,
    }
    setItem(type, item)
  }

  useEffect(() => {
    if (
      state.sports.value.length === 0 ||
      state.wagerTypes.value.length === 0
    ) {
      setFormValues({
        ...state,
        sports: {
          value: getSportDefaultValues() || [],
          error: '',
        },
        wagerTypes: {
          value: getWagerTypeDefaultValues() || [],
          error: '',
        },
      })
    }
  }, [state.sports, state.wagerTypes]) // eslint-disable-line

  return {
    rolloverRestrictions: {
      state,
      handleOnChange,
      disable,
      setFormValues,
      setDisable,
    },
    setCurrentRolloverRestrictions,
    handleOnClickSetEnableRollover,
    handleOnClickSetDisableRollover,
    handleOnClickSetDiscardContributionRollover,
    getProductsDefaultValues,
  }
}

export default useRolloverRestrictions
